/**
 * File: UserProfileOnboardingGate.tsx
 * Description: Blocks protected content until the current user confirms their
 *   persona and, when required, line of business through the welcome flow.
 * Author: GitHub Copilot
 * Created: 2026-05-28
 * Last Modified: 2026-05-28
 */
import React from 'react';

import { isLineOfBusinessRequired } from '../constants/persona';
import { useUserProfile } from '../contexts/UserProfileContext';
import { LineOfBusinessWelcomePage } from '../pages/LineOfBusinessWelcome/LineOfBusinessWelcomePage';

import { LoadingSpinner } from './LoadingSpinner/LoadingSpinner';

const PROFILE_LOADING_LABEL = 'Loading your profile...';
const PROFILE_ERROR_TITLE = 'We could not load your profile';
const PROFILE_ERROR_MESSAGE =
  'Please retry to continue with Archie.';
const RETRY_BUTTON_LABEL = 'Retry';

interface UserProfileOnboardingGateProps {
  children: React.ReactNode;
}

export const UserProfileOnboardingGate: React.FC<
  UserProfileOnboardingGateProps
> = ({ children }) => {
  const { profile, loading, error, refresh } = useUserProfile();
  const isProfilePending = profile === null && error === null;
  const requiresLineOfBusiness = isLineOfBusinessRequired(profile?.persona);
  const isProfileConfirmed =
    Boolean(profile?.persona) &&
    Boolean(profile?.line_of_business_confirmed_at) &&
    (!requiresLineOfBusiness || Boolean(profile?.line_of_business));

  if (loading || isProfilePending) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-10">
        <LoadingSpinner label={PROFILE_LOADING_LABEL} size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center px-6 py-10">
        <div className="w-full max-w-lg rounded-3xl border border-[rgba(28,28,28,0.14)] bg-white p-8 shadow-[0_18px_40px_rgba(28,28,28,0.08)]">
          <h2 className="text-2xl font-semibold text-[#343434]">
            {PROFILE_ERROR_TITLE}
          </h2>
          <p className="mt-3 text-sm leading-6 text-[#757575]">
            {error || PROFILE_ERROR_MESSAGE}
          </p>
          <button
            className="mt-6 inline-flex rounded-full bg-[#dd1d21] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#fb594e] focus:outline-none focus:ring-2 focus:ring-[#0097a9] focus:ring-offset-2"
            onClick={() => void refresh()}
            type="button"
          >
            {RETRY_BUTTON_LABEL}
          </button>
        </div>
      </div>
    );
  }

  if (!isProfileConfirmed) {
    return <LineOfBusinessWelcomePage />;
  }

  return <>{children}</>;
};

export default UserProfileOnboardingGate;